import type React from "react";
import type { Omittable } from "@salestrak/ui";

import {
  Button,
  CountSticker,
  ButtonClear,
  ButtonExpansion,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  type ButtonProps,
  type IconName,
  type IconSize,
} from "@salestrak/ui";

import { HotkeyTooltipLabel } from "#app/components/keyboard-shortcuts/hotkey-tooltip-label";

type FilterToggleSize = "xs" | "sm" | "default" | "lg";

type FilterToggleProps = Omit<
  ButtonProps,
  "children" | "size" | "variant" | "expanded" | "loading"
> & Omittable<{
  icon: IconName;
  /** Selector-trigger label, shown as-is (a date preset, a slice). On the
   *  plain filter chip it is the singular noun the count pluralizes
   *  ("Filter" → "3 Filters"). */
  label?: string;
  /** Active-filter count. Zero keeps the chip collapsed to its icon; a
   *  positive count expands it to the pluralized label ("1 Filter"). */
  count?: number;
  /** Defaults to `count > 0`. */
  selected?: boolean;
  /** Flag mode: the label rides the expansion slot instead of the face, so
   *  the chip rests as its icon and reveals the label while selected — the
   *  same collapse the count chip makes, for an on/off state whose label
   *  names the state rather than a selection. Requires `label`; ignored in
   *  count mode. */
  flag?: boolean;
  /** Readout mode: the applied value ITSELF rides the expansion slot — an
   *  avatar stack, a named state — where count mode would put "2 Filters".
   *  Pass null while nothing is applied (the chip rests as its icon) and
   *  `label` for the accessible name, which never paints on the face. Pass
   *  `count` alongside for the phone's corner sticker, which has no room
   *  for faces; the count rides the accessible name there. */
  readout?: React.ReactNode;
  /** Stands where the glyph stood while set: the applied value's own visual
   *  (an avatar stack) leading the chip the way the me-mode chip leads with
   *  its avatar. The phone's collapsed face keeps the glyph and lets the
   *  corner sticker count. */
  face?: React.ReactNode;
  /** Renders the clear (×) affordance while selected. */
  onClear?: () => void;
  /** Pin the compact face on every viewport: the chip holds its icon
   *  footprint in every state and the tint alone carries the ON state — no
   *  expansion, no corner count, no hover-revealed clear. For tight chrome
   *  like a widget card header, where the expanded pill overruns the actions
   *  beside it; the count still rides the accessible name. */
  compact?: boolean;
  size?: FilterToggleSize;
  iconSize?: IconSize;
  /** Tint for the icon face — for fixed-color backdrops (map controls) where
   *  the theme foreground doesn't match sibling icons. */
  iconTintColor?: string;
  /**
   * Structural variant of the OFF (unselected) state. `ghost` is the borderless
   * toolbar chip; `outline` gives it a resting border.
   */
  offVariant?: "ghost" | "outline";
  /** Hover tooltip on the chip itself. */
  tooltip?: string;
  /** Keycaps joined to `tooltip` while this chip's panel is the filter
   *  hotkey's current target — the FilterPanel passes them. */
  tooltipKeys?: readonly string[];
  /** Hover tooltip on the clear (×) disc, doubling as its accessible name. */
  clearTooltip?: string;
  /** Keycaps joined to `clearTooltip` the same way (the label alone stays
   *  the accessible name). */
  clearTooltipKeys?: readonly string[];
  ref?: React.Ref<HTMLButtonElement>;
}>;

/**
 * The standard toolbar filter chip: icon-only at rest, expanding to the
 * pluralized count ("1 Filter", "3 Filters") with a hover-revealed clear
 * overlay while filters are active. Pass `label` without `count` for
 * selector-trigger mode (a date preset, a slice chip), where the label IS
 * the selection and stays on the face; add `flag` for an on/off chip that
 * collapses to its icon while off. Composes as a `render` child of
 * Menu/Popover/Select/EntitySelector triggers.
 *
 * On a phone the chip never expands: it holds its icon footprint in every
 * state so the toolbar row cannot wrap, the tint alone carries the ON state,
 * and count mode marks the count as a corner `CountSticker` instead. The
 * hover-revealed clear has no hover to reveal on, so it is not mounted there.
 * `compact` pins that footprint on every viewport and drops the badge too —
 * the tint is the whole ON state, and clearing belongs to the panel the chip
 * opens.
 */
export function FilterToggle({
  icon,
  label,
  count,
  selected,
  flag = false,
  readout,
  face,
  onClear,
  compact = false,
  size = "default",
  // 14px, the toolbar-sibling rung: our glyphs draw heavier than the
  // reference set's, so the 16px step reads oversized on the 24px chip.
  iconSize = "sm",
  iconTintColor,
  offVariant = "ghost",
  tooltip,
  tooltipKeys,
  clearTooltip,
  clearTooltipKeys,
  ref,
  id,
  ...props
}: FilterToggleProps) {
  // The collapsed face: a phone's, or one a caller pinned for tight chrome.
  const collapsed = useIsMobile() || compact;
  // Value mode is the prop being PASSED (null while nothing is applied);
  // the value's presence is the ON state.
  const valueMode = readout !== undefined;
  const hasValue = readout !== undefined && readout !== null;
  const isSelected = selected ?? (hasValue || (count !== undefined && count > 0));
  // A value chip's count is for the phone's sticker only — on the wide face
  // the value itself is the readout.
  const showCount = count !== undefined && count > 0 && !valueMode;
  const showClear = isSelected && !!onClear && !collapsed;
  const noun = label ?? "Filter";
  const plural = `${noun}s`;
  const countLabel = showCount
    ? `${count} ${count === 1 ? noun : plural}`
    : null;
  // Count mode owns the expansion slot; a flag chip borrows it for its label.
  const isFlag = flag && count === undefined && !!label && !valueMode;
  const expansionLabel = countLabel ?? (isFlag && isSelected ? label : null);
  // Both collapsed faces state the count in the accessible name — a badge is
  // aria-hidden, and the compact face draws none at all.
  const collapsedCount =
    collapsed && count !== undefined && count > 0 ? count : null;
  const badgeCount = compact ? null : collapsedCount;

  // The × disc gets its own tooltip: the app-wide TooltipProvider delay group
  // closes the chip's bubble the moment the disc's opens, so hovering across
  // the chip reads as one label handing off to the other.
  const clearAffordance = showClear ? (
    clearTooltip ? (
      <Tooltip>
        <TooltipTrigger
          render={
            <ButtonClear aria-label={clearTooltip} onClick={() => onClear()} />
          }
        />
        <TooltipPopup side="top">
          {clearTooltipKeys ? (
            <HotkeyTooltipLabel keys={clearTooltipKeys} label={clearTooltip} />
          ) : (
            clearTooltip
          )}
        </TooltipPopup>
      </Tooltip>
    ) : (
      <ButtonClear aria-label="Clear selection" onClick={() => onClear()} />
    )
  ) : null;

  // Every Base UI popup trigger (Menu, Popover, Drawer) stamps its own `id`
  // on the element and resolves the hovered/pressed trigger by that DOM id.
  // When this chip is the `render` child of such a trigger, that id arrives
  // in props and must land on the TooltipTrigger, not past it on the Button:
  // the tooltip registers under whatever id it renders, and an id spread
  // onto the inner Button would win the merge, leaving the bubble registered
  // under an id no element carries — hover resolves, nothing opens. The
  // spread (not `id={undefined}`) matters: an explicit undefined also wins
  // the merge and strips the id the TooltipTrigger stamped.
  const button = (
    <Button
      ref={ref}
      {...(tooltip ? null : { id })}
      size={size}
      // Selected always reads as the theme-colored outline; unselected
      // follows `offVariant`.
      color={isSelected ? "primary" : "neutral"}
      variant={isSelected || offVariant === "outline" ? "outline" : "ghost"}
      aria-pressed={isSelected}
      // The clear disc reveals only under `data-expanded`, so a clearable
      // selector-label chip must expand too. The label reserves no trailing
      // room for it: the opaque disc overlays the label's last characters on
      // hover, keeping the chip's insets symmetric at rest.
      expanded={
        !collapsed && isSelected && (showCount || showClear || isFlag || hasValue)
      }
      // Icon-only mode has no visible text to name the control; a flag chip
      // hides its label while off, so the name rides the attribute throughout.
      aria-label={
        collapsedCount !== null
          ? `${plural}, ${collapsedCount} active`
          : isFlag || valueMode
            ? (label ?? "Filters")
            : label
              ? undefined
              : "Filters"
      }
      {...props}
    >
      {face && !collapsed ? (
        face
      ) : (
        <Icon
          name={icon}
          size={iconSize}
          // A resolved (non-token) color from a fixed-color backdrop — the
          // only way to paint the glyph's currentColor from a runtime value.
          style={iconTintColor ? { color: iconTintColor } : undefined}
        />
      )}
      {/* The label rides the chip's own type rung — 12px on the 24px `xs`
          chip, 14px on the taller sizes — so `size={null}` inherits it
          instead of pinning one size across every rung. */}
      {label && count === undefined && !isFlag && !valueMode ? (
        <Text size={null} truncate={1}>
          {label}
        </Text>
      ) : null}
      {/* Count, flag and value modes only: the slot must stay mounted through
          count 0 / off / no value so the collapse can animate. Label mode
          renders no expansion at all — an empty slot would re-open the
          lopsided trailing gap. */}
      {count !== undefined || isFlag || valueMode ? (
        <ButtonExpansion>
          {valueMode && !collapsed ? (
            readout
          ) : expansionLabel ? (
            <Text size={null} truncate={1}>
              {expansionLabel}
            </Text>
          ) : null}
        </ButtonExpansion>
      ) : null}
      {badgeCount !== null ? <CountSticker count={String(badgeCount)} /> : null}
      {clearAffordance}
    </Button>
  );

  if (!tooltip) return button;

  return (
    <Tooltip>
      <TooltipTrigger id={id} render={button} />
      <TooltipPopup side="top">
        {tooltipKeys ? (
          <HotkeyTooltipLabel keys={tooltipKeys} label={tooltip} />
        ) : (
          tooltip
        )}
      </TooltipPopup>
    </Tooltip>
  );
}

export type { FilterToggleProps, FilterToggleSize };
